import React, { useState, useEffect } from 'react';
import { RotateCcw, X, Minus, Plus, Trash2, Undo2 } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { CartItem } from '../../types/book.types';
import { Transaction } from '../../types/transaction.types';
import './ReturnModal.css';

export interface ReturnModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onConfirm: (txId: string, items: CartItem[], isFull: boolean) => void;
}

export function ReturnModal({ isOpen, transaction, onClose, onConfirm }: ReturnModalProps) {
  const [returnItems, setReturnItems] = useState<CartItem[]>(
    transaction ? transaction.items.map(item => ({ ...item, quantity: 0 })) : []
  );

  useEffect(() => {
    if (transaction) {
      setReturnItems(transaction.items.map(item => ({ ...item, quantity: 0 })));
    }
  }, [transaction]);

  if (!isOpen || !transaction) return null;

  const updateReturnQty = (id: string, delta: number, max: number) => {
    setReturnItems(prev =>
      prev.map(item => {
        if (item.id === id) {
          const newQty = Math.max(0, Math.min(max, item.quantity + delta));
          return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  };

  const hasReturns = returnItems.some(i => i.quantity > 0);
  const returnTotal = returnItems.reduce(
    (sum, item) => sum + item.price * item.quantity * (1 - item.discount / 100),
    0
  );

  const handleFullReturn = () => {
    onConfirm(transaction.id, transaction.items, true);
  };

  const handlePartialReturn = () => {
    onConfirm(transaction.id, returnItems.filter(i => i.quantity > 0), false);
  };

  return (
    <div className="return-modal-overlay">
      <div className="return-modal-container">
        {/* Header */}
        <div className="return-modal-header">
          <div className="return-modal-header-left">
            <div className="return-modal-icon">
              <RotateCcw size={24} />
            </div>
            <div>
              <h3 className="return-modal-title">Gestionar Devolución</h3>
              <p className="return-modal-subtitle">{transaction.id}</p>
            </div>
          </div>
          <button className="return-modal-close-btn" onClick={onClose} aria-label="Cerrar">
            <X size={24} />
          </button>
        </div>

        {/* Items */}
        <div className="return-modal-items">
          {transaction.items.map((item, idx) => {
            const currentReturn = returnItems.find(ri => ri.id === item.id);
            const currentQty = currentReturn?.quantity ?? 0;
            const atMax = currentQty >= item.quantity;

            return (
              <div key={item.id + idx} className="return-modal-item">
                <div className="return-modal-item-info">
                  <h4 className="return-modal-item-title">{item.title}</h4>
                  <p className="return-modal-item-meta">
                    Comprado: {item.quantity} uds • {formatCurrency(item.price)}/u
                  </p>
                </div>

                <div className="return-modal-item-controls">
                  <div className="return-modal-qty-control">
                    <button
                      className="return-modal-qty-btn"
                      onClick={() => updateReturnQty(item.id, -1, item.quantity)}
                      aria-label="Reducir cantidad"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="return-modal-qty-value">{currentQty}</span>
                    <button
                      className={`return-modal-qty-btn${atMax ? ' return-modal-qty-btn--at-max' : ''}`}
                      onClick={() => updateReturnQty(item.id, 1, item.quantity)}
                      disabled={atMax}
                      aria-label="Aumentar cantidad"
                    >
                      <Plus size={12} />
                    </button>
                  </div>

                  <div className="return-modal-item-amount">
                    <p>-{formatCurrency(currentQty * item.price * (1 - item.discount / 100))}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="return-modal-footer">
          <div className="return-modal-total-row">
            <span className="return-modal-total-label">Total a devolver</span>
            <span className="return-modal-total-amount">{formatCurrency(returnTotal)}</span>
          </div>

          <div className="return-modal-actions">
            <button className="return-modal-btn-full" onClick={handleFullReturn}>
              <Trash2 size={20} />
              DEVOLUCIÓN TOTAL
            </button>
            <button
              className="return-modal-btn-partial"
              disabled={!hasReturns}
              onClick={handlePartialReturn}
            >
              <Undo2 size={20} />
              DEVOLVER SELECCIÓN
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
